import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { syncTesterModeFromSearch, testerLandingHref } from '../utils/testerMode';

/*
 * Privacy Policy + Terms of Service.
 *
 * Plain-language wording written to accurately describe how the platform
 * works today (data inventory sourced from the live database schema;
 * processors sourced from the actual service integrations). Still pending
 * final review by counsel before pilot launch — noted discreetly in the
 * header rather than a draft banner.
 */

const wrap = {
  maxWidth: 760,
  margin: '0 auto',
  padding: '40px 22px 80px',
  color: '#1f2430',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  lineHeight: 1.6,
};
const h1 = { fontSize: 26, margin: '0 0 6px' };
const sectionTitle = { fontSize: 21, margin: '36px 0 4px' };
const h2 = { fontSize: 18, margin: '28px 0 6px' };
const h3 = { fontSize: 15, margin: '16px 0 4px', color: '#3a4658' };
const muted = { color: '#6f7b91', fontSize: 13 };
const list = { margin: '6px 0 0', paddingLeft: 20 };

// Collected-data inventory, grouped. Sourced from the database schema
// (leads, client_accounts, client_buyer_profiles, conversations, messages,
// property_client_states, saved_searches, appointments, match_events) so the
// list stays factually accurate.
const collected = [
  {
    group: 'Information you provide',
    items: [
      'Identity and contact details — first and last name, email address, phone number',
      'Home-search criteria — budget range, minimum bedrooms and bathrooms, property types, preferred areas, must-haves, deal-breakers, and move timeline',
      'Financing details — pre-approval status, pre-approval amount, and lender name',
      'Conversation content — the messages you send and any voice transcripts or audio recordings',
      'Listing activity — listings you save, pass on, or tour, and the saved searches you create',
      'Showing and booking details — requested showing times and appointment notes',
    ],
  },
  {
    group: 'Inferred qualification signals',
    items: [
      'AI-generated buyer summary, qualification and lead status, conversation sentiment, action items, and listing match scores and reasons',
    ],
  },
  {
    group: 'Technical and usage data',
    items: [
      'Voice call duration and recordings, AI token usage, login timestamps, and record creation/update timestamps',
    ],
  },
];

function PrivacyBody() {
  return (
    <section id="privacy">
      <h2 style={sectionTitle}>Privacy Policy</h2>

      <h2 style={h2}>1. Who we are</h2>
      <p>
        Agents4Agents is operated by <strong>Fidler Innovations Inc.</strong>, a Canadian company
        based in Newfoundland and Labrador (NRC IRAP Project #1038537). We provide AI assistant
        technology to independent, licensed real estate agents. Our designated Privacy Officer is
        responsible for this policy and for handling privacy inquiries — see section 12 for how to
        reach us.
      </p>

      <h2 style={h2}>2. What we collect</h2>
      <p>
        When you use a real estate agent&apos;s AI assistant, the platform collects the following
        categories of information:
      </p>
      {collected.map((c) => (
        <div key={c.group}>
          <h3 style={h3}>{c.group}</h3>
          <ul style={list}>
            {c.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
      <p>
        You choose how much to share. Contact details are only required when you ask for something
        that needs follow-up — for example booking a showing or requesting a callback.
      </p>

      <h2 style={h2}>3. How we use it</h2>
      <p>
        We use this information for a limited set of purposes: understanding your home-search or
        selling needs, surfacing listings that match your criteria, booking showings and callbacks
        you request, passing your details and conversation summary to your real estate agent so
        they can follow up, and maintaining your saved search and account if you create one. We do
        not use your personal information for any purpose beyond operating the assistant and the
        agent relationship it supports, and we do not sell it.
      </p>

      <h2 style={h2}>4. AI disclosure</h2>
      <p>
        The assistant you chat with is an artificial-intelligence tool, not a person. It is
        disclosed as an AI assistant on the chat page, and everything you share in the conversation
        is made available to the human real estate agent the assistant works on behalf of. A real,
        licensed agent — not the AI — handles your showings, offers, and advice.
      </p>

      <h2 style={h2}>5. Who we share it with</h2>
      <p>
        Your information is shared with <strong>your real estate agent</strong> — that is the point
        of the service. To run the assistant we also use a small number of service providers who
        process data on our behalf: Anthropic (AI conversation processing), OpenAI and ElevenLabs
        (voice transcription and speech), an email delivery provider (booking notifications), and
        our hosting provider (Render). Each provider receives only what it needs to perform its
        function. We do not sell personal information, and we do not share it with advertisers.
      </p>

      <h2 style={h2}>6. Where data is stored</h2>
      <p>
        The platform is hosted on Render&apos;s cloud infrastructure. Some processing — including
        AI conversation and voice processing — takes place in the United States through the
        providers named in section 5. Information handled outside Canada may be subject to the
        laws of those jurisdictions.
      </p>

      <h2 style={h2}>7. Retention</h2>
      <p>
        We keep your information for as long as it is needed to support your relationship with
        your agent — for example while your account or saved search is active, or while an
        enquiry is being followed up. You can have your data deleted at any time (see section 8),
        and your agent can permanently delete a client&apos;s records on request.
      </p>

      <h2 style={h2}>8. Your rights</h2>
      <p>
        You can ask to access the personal information we hold about you, correct it, withdraw
        your consent, or have it deleted. Your agent can export a complete copy of your data or
        permanently delete it on request, and you can also contact our Privacy Officer directly
        (section 12). Withdrawing consent or deleting your data may mean the assistant and your
        agent can no longer follow up on your search.
      </p>

      <h2 style={h2}>9. Consent</h2>
      <p>
        We rely on your consent, given when you start chatting — the chat page tells you that you
        are talking to an AI assistant and that details you share are passed to the agent — and
        again when you provide contact details for a showing or callback. Consent is recorded with
        a timestamp in our records.
      </p>

      <h2 style={h2}>10. Cross-agent intelligence layer (future)</h2>
      <p>
        We are exploring a future feature where anonymized, de-identified market insights are
        shared across agents on the platform. <strong>This feature is not active.</strong> No
        personal information is shared between agents today, and before any such feature launches
        this policy will be updated with the anonymization safeguards it uses.
      </p>

      <h2 style={h2}>11. Security</h2>
      <p>
        Personal information is protected with industry-standard safeguards: encryption in transit,
        encrypted storage with our hosting provider, authenticated access controls so each agent
        can only see their own clients, and access limited to the small team operating the
        platform.
      </p>

      <h2 style={h2}>12. Changes &amp; contact</h2>
      <p>
        If this policy changes materially, we will update the &quot;last updated&quot; date at the
        top of this page and, where appropriate, note the change in the product. Privacy questions
        and requests can be sent to our Privacy Officer at Fidler Innovations Inc. — or raised
        through your real estate agent, who can act on your behalf. If you are not satisfied with
        our response, you have the right to complain to the Office of the Privacy Commissioner of
        Canada (oipc.gov.nl.ca for Newfoundland and Labrador matters, priv.gc.ca federally).
      </p>
    </section>
  );
}

function TermsBody() {
  return (
    <section id="terms">
      <h2 style={sectionTitle}>Terms of Service</h2>

      <h2 style={h2}>1. The assistant is an AI tool</h2>
      <p>
        The chat assistant is an automated artificial-intelligence tool provided on behalf of an
        independent, licensed real estate agent. The assistant itself is not a licensed
        REALTOR®, and its responses are general information — not professional real estate,
        legal, tax, or financial advice. Always confirm important details with your agent before
        acting on them.
      </p>

      <h2 style={h2}>2. No agency relationship</h2>
      <p>
        Chatting with the assistant does not create a buyer or seller representation agreement,
        an agency relationship, or any obligation between you and the agent. Representation is
        established directly with the agent, in writing, in accordance with the real estate
        regulations of your province.
      </p>

      <h2 style={h2}>3. Listing information</h2>
      <p>
        Listing information presented by the assistant may come from third-party sources,
        including MLS® systems operated by real estate boards and The Canadian Real Estate
        Association (CREA), or from the agent directly. While we work to keep it current, listing
        details can change or contain errors, and no warranty is made as to their accuracy or
        completeness. Verify any detail that matters — price, availability, measurements, fees —
        with your agent before making decisions.
      </p>

      <h2 style={h2}>4. Acceptable use</h2>
      <p>
        You agree to provide accurate information, to use the assistant for genuine real estate
        enquiries only, and not to misuse the service — including attempting to scrape or
        systematically extract listing data, probe or disrupt the platform, impersonate others, or
        use the service for any unlawful purpose.
      </p>

      <h2 style={h2}>5. Limitation of liability &amp; warranties</h2>
      <p>
        The service is provided &quot;as is&quot; and &quot;as available,&quot; without warranties
        of any kind, express or implied. To the maximum extent permitted by law, Fidler
        Innovations Inc. is not liable for indirect, incidental, or consequential damages arising
        from your use of the assistant, including decisions made in reliance on AI-generated
        responses or listing information. Nothing in these terms limits liability that cannot be
        limited under applicable law.
      </p>

      <h2 style={h2}>6. Governing law</h2>
      <p>
        These terms are governed by the laws of the Province of Newfoundland and Labrador and the
        federal laws of Canada applicable in it, and any dispute is subject to the courts of that
        province.
      </p>

      <h2 style={h2}>7. Changes &amp; contact</h2>
      <p>
        We may update these terms from time to time; the &quot;last updated&quot; date at the top
        of this page reflects the current version, and continued use of the assistant after an
        update constitutes acceptance. Questions about these terms can be directed to Fidler
        Innovations Inc., operator of Agents4Agents, or raised through your real estate agent.
      </p>
    </section>
  );
}

export default function Legal() {
  const { pathname, search } = useLocation();
  const fromSlug = new URLSearchParams(search).get('from');
  const backTo = fromSlug && syncTesterModeFromSearch(search) ? testerLandingHref(fromSlug) : '/';
  const focus = pathname.includes('terms') ? 'terms' : 'privacy';
  const label = focus === 'terms' ? 'Terms of Service' : 'Privacy Policy';

  return (
    <div style={{ background: '#f7f9fc', minHeight: '100vh' }}>
      <div style={wrap}>
        <Link to={backTo} style={{ ...muted, textDecoration: 'none' }}>← Back</Link>
        <h1 style={h1}>{label}</h1>
        <p style={muted}>
          Agents4Agents, operated by Fidler Innovations Inc. (NRC IRAP #1038537) ·
          Last updated: July 6, 2026 · Pilot program — under final legal review.
        </p>

        {focus === 'terms' ? <TermsBody /> : <PrivacyBody />}

        <p style={{ ...muted, marginTop: 40 }}>
          {focus === 'terms' ? (
            <Link to="/privacy" style={{ color: '#6f7b91' }}>View Privacy Policy</Link>
          ) : (
            <Link to="/terms" style={{ color: '#6f7b91' }}>View Terms of Service</Link>
          )}
        </p>

        <p style={{ ...muted, marginTop: 18, fontSize: 11.5 }}>
          REALTOR®, REALTORS®, and the REALTOR® logo are certification marks owned by REALTOR®
          Canada Inc. and licensed exclusively to The Canadian Real Estate Association (CREA). The
          MLS® trademark and the MLS® logo are owned by CREA and identify the quality of services
          provided by real estate professionals who are members of CREA.
        </p>
      </div>
    </div>
  );
}
